<script setup lang="ts">
/**
 * `Toggle.vue` — iOS-style switch.
 *
 * Mirrors the Unity `Content/Controls/Toggle*.prefab` family used across
 * the inspector and settings panels.
 */
defineOptions({ name: "Toggle" });
const props = withDefaults(
  defineProps<{
    modelValue: boolean;
    label?: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);
const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
}>();

function toggle() {
  if (props.disabled) return;
  emit("update:modelValue", !props.modelValue);
}
</script>

<template>
  <button
    type="button"
    class="cf-toggle"
    :class="{ 'cf-toggle--on': modelValue, 'cf-toggle--disabled': disabled }"
    :aria-pressed="modelValue"
    :aria-label="label"
    :disabled="disabled"
    @click="toggle"
  >
    <span class="cf-toggle__track" />
    <span class="cf-toggle__thumb" />
  </button>
</template>

<style scoped>
.cf-toggle {
  position: relative;
  width: 36px;
  height: 20px;
  display: inline-block;
  flex: 0 0 auto;
}

.cf-toggle__track {
  position: absolute;
  inset: 0;
  border-radius: var(--cf-radius-pill);
  background: var(--cf-color-toggle-off);
  transition: background var(--cf-motion-default) var(--cf-ease-default);
}

@supports (corner-shape: squircle) {
  .cf-toggle__track,
  .cf-toggle__thumb {
    corner-shape: round;
  }
}

.cf-toggle--on .cf-toggle__track {
  background: var(--cf-color-toggle-on);
}

.cf-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: var(--cf-color-toggle-thumb);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  transition: transform var(--cf-motion-default) var(--cf-ease-default);
}

.cf-toggle--on .cf-toggle__thumb {
  transform: translateX(16px);
}

.cf-toggle--disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>
